import {
  ChatBubbleLeftIcon,
  CheckIcon,
  EnvelopeIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import classNames from "../../../../utils/classNames";
import PriceFormat from "../../../../utils/priceFormat";
import useVatMarginScheme from "../../../../data/useVatMarginScheme";

export default function QuotedPriceCard({
  value,
  applyVAT,
  editing,
  priceSaved,
  priceChangeReason,
  notifyEmail,
  notifySms,
  onStartEditing,
  onPriceChange,
  onSavePrice,
  onCancelEditing,
  onReasonChange,
  onNotifyEmailChange,
  onNotifySmsChange,
  onUpdateAndNotify,
  onDoneEditing,
}: {
  value: number;
  applyVAT?: boolean;
  editing: boolean;
  priceSaved: boolean;
  priceChangeReason: string;
  notifyEmail: boolean;
  notifySms: boolean;
  onStartEditing: () => void;
  onPriceChange: (value: number) => void;
  onSavePrice: () => void;
  onCancelEditing: () => void;
  onReasonChange: (value: string) => void;
  onNotifyEmailChange: (value: boolean) => void;
  onNotifySmsChange: (value: boolean) => void;
  onUpdateAndNotify: () => void;
  onDoneEditing: () => void;
}) {
  const { standardRate } = useVatMarginScheme();
  return (
    <div
      className={classNames(
        "min-w-[180px] rounded-xl border-2 px-5 py-3 transition-colors",
        editing ? "border-amber-300 bg-amber-50" : "border-primary-200 bg-primary-50"
      )}
    >
      <p
        className={classNames(
          "mb-1 text-[10px] font-bold uppercase tracking-wider",
          editing ? "text-amber-600" : "text-primary-600"
        )}
      >
        Quoted Price
      </p>

      {editing ? (
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-amber-400">&pound;</span>
            <input
              type="number"
              value={value}
              onChange={(e) => onPriceChange(parseFloat(e.target.value) || 0)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSavePrice();
                if (e.key === "Escape") onCancelEditing();
              }}
              className="w-28 rounded-md border border-amber-300 bg-white px-2 py-1 text-xl font-bold text-gray-900 focus:border-amber-500 focus:ring-amber-500"
              autoFocus
            />

            {!priceSaved ? (
              <>
                <button
                  type="button"
                  onClick={onSavePrice}
                  className="rounded-md bg-green-600 p-1 text-white hover:bg-green-700"
                  title="Save price"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={onCancelEditing}
                  className="rounded-md bg-gray-200 p-1 text-gray-700 hover:bg-gray-300"
                  title="Cancel price change"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </>
            ) : (
              <span className="rounded-md bg-green-100 p-1.5 text-green-600">
                <CheckIcon className="h-4 w-4" />
              </span>
            )}
          </div>

          {priceSaved && (
            <div className="mt-2.5 space-y-2.5 border-t border-amber-200 pt-2.5">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                  Reason for price change
                </label>
                <input
                  type="text"
                  value={priceChangeReason}
                  onChange={(e) => onReasonChange(e.target.value)}
                  placeholder="e.g. Customer negotiation, vehicle condition, market adjustment..."
                  className="mt-1 w-full rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-amber-500"
                />
              </div>

              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                  Notify Customer
                </p>

                <div className="mb-2 flex items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={notifyEmail}
                      onChange={(e) => onNotifyEmailChange(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <EnvelopeIcon className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-700">Email</span>
                  </label>

                  <label className="flex cursor-pointer items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={notifySms}
                      onChange={(e) => onNotifySmsChange(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <ChatBubbleLeftIcon className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-700">SMS</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onUpdateAndNotify}
                  disabled={!priceChangeReason.trim()}
                  className="flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Update & Notify
                </button>

                <button
                  type="button"
                  onClick={onDoneEditing}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  Skip
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-3xl font-extrabold text-gray-900">{PriceFormat(value)}</span>
          <button
            type="button"
            onClick={onStartEditing}
            className="rounded-md p-1 text-primary-400 transition-colors hover:bg-primary-100 hover:text-primary-600"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {applyVAT && (
        <p className={classNames("mt-0.5 text-[10px]", editing ? "text-amber-500" : "text-primary-500")}>
          Inc. VAT: {PriceFormat(value * (1 + standardRate / 100))}
        </p>
      )}
    </div>
  );
}
